import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { UPDATE_MY_TASKS, TASK_FILE_UPLOAD } from "./apiRoutes";
import { removeEmptyFields } from "@/lib/utils";

/**
 * -----------------------------
 * Types
 * -----------------------------
 */
export interface UpdateMyTasksPayload {
  id: string; // used in URL
  taskStatus: string;
  taskAttachment?: string | File; // can be string (existing id) or File (new upload)
  taskAttachmentName?: string; // 👈 added to include file_name
  taskNotes: string;
}

export interface UpdateMyTaskResponse {
  status: number;
  error: boolean;
  message: string;
  data: any;
}

/**
 * -----------------------------
 * File Upload Utility
 * -----------------------------
 */
const uploadMyTaskFileAndGetInfo = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("file_type", "task_attachment");

  const res = await axiosAuth.post(TASK_FILE_UPLOAD, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const data = res?.data?.data;
  return {
    attachment_id: data?.attachment_id as string,
    file_name: data?.file_name as string,
  };
};

/**
 * -----------------------------
 * Mutation Hook (Final)
 * -----------------------------
 */
export const useUpdateMyTasksMutation = (
  options?: UseMutationOptions<
    UpdateMyTaskResponse,
    Error,
    UpdateMyTasksPayload
  >
) => {
  return useMutation({
    mutationFn: async (payload: UpdateMyTasksPayload) => {
      const { id, ...updateData } = payload;

      // ✅ Handle file upload if a File object is provided
      let taskAttachmentUuid = updateData.taskAttachment;
      let taskAttachmentName = updateData.taskAttachmentName;

      if (taskAttachmentUuid && typeof taskAttachmentUuid !== "string") {
        const fileInfo = await uploadMyTaskFileAndGetInfo(
          taskAttachmentUuid as File
        );
        taskAttachmentUuid = fileInfo.attachment_id;
        taskAttachmentName = fileInfo.file_name;
      }

      // Clean up empty/null fields
      const cleanedPayload = removeEmptyFields({
        ...updateData,
        taskAttachment: taskAttachmentUuid,
        taskAttachmentName, // 👈 added file_name
      });

      const response = await axiosAuth.put<UpdateMyTaskResponse>(
        `${UPDATE_MY_TASKS}${id}`,
        cleanedPayload
      );

      return response.data;
    },
    ...options,
  });
};
